/**
 * One workspace (a client/matter) or one of its folders. Header: the
 * breadcrumb (library / workspace / folders), the search scoped to where you
 * are, and the workspace star; then the tabs Documents · Emails · Folders ·
 * Matter info. The document table opens documents in the preview pane, and
 * folders as their own page.
 *
 * At the workspace's top level, Documents and Emails list the whole
 * workspace, newest first (iManage files documents in folders, not at the
 * top); inside a folder they list that folder.
 *
 * @module components/apps/imanage/IManageWorkspaceView
 */
import type { IManageDocument, IManageEntry } from "@amoreg/contracts/imanage";
import { Drawer } from "@lucasmarkes/hairline/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronRightIcon, FolderIcon, MailIcon, SearchXIcon, SparklesIcon, StarIcon } from "lucide-react";
import { useCallback, useMemo, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { useStartChatWithText } from "../../../hooks/useStartChatWithText";
import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../ui/tooltip";
import { useIManageArea } from "./imanageAreaContext";
import { clientMatter, formatDateTime, partitionEntries, sortEntries } from "./imanageFormat";
import { refFromEntry, refKey, toggledFavorite, useIManageList } from "./imanageLocalLists";
import {
  documentKey,
  libraryOfId,
  patchSearch,
  sortFromSearch,
  type IManageTab,
  type IManageViewSearch,
} from "./imanagePaths";
import {
  invalidateIManage,
  useChildren,
  useContainer,
  useIManageLibrary,
  useSearch,
} from "./imanageQueries";
import { IManageDocumentTable, type IManageSort, type IManageTableGroup } from "./IManageDocumentTable";
import { EmptyList, IManagePageHeader, IManageSearchField, IManageSplit, IManageViewTabs } from "./IManageLayout";
import { InlineError, MatterNumber } from "./IManageParts";
import { IManagePreviewPane } from "./IManagePreviewPane";
import { IManageReviewPanel } from "./IManageReviewPanel";

export interface IManageViewProps {
  readonly search: IManageViewSearch;
  readonly onSearchChange: (next: IManageViewSearch, options?: { readonly replace?: boolean }) => void;
}


/** Favorites for table rows: star state and toggle, shared by every list page. */
export function useFavoriteRows(organizationId: string, library: string | null, place: { workspaceId?: string | null; folderId?: string | null } = {}) {
  const [favorites, setFavorites] = useIManageList(organizationId, "favorites");
  const keys = useMemo(() => new Set(favorites.map(refKey)), [favorites]);
  const isFavorite = useCallback(
    (entry: IManageEntry) => keys.has(entry.kind === "document" ? documentKey(entry.id) : entry.id),
    [keys],
  );
  const toggle = useCallback(
    (entry: IManageEntry) =>
      setFavorites((list) =>
        toggledFavorite(
          list,
          refFromEntry(entry, { library: libraryOfId(entry.id) ?? library ?? "", ...place }, Date.now()),
        ),
      ),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [setFavorites, library, place.workspaceId, place.folderId],
  );
  return { isFavorite, toggle };
}

export function IManageWorkspaceView({
  workspaceId,
  folderId,
  search,
  onSearchChange,
}: IManageViewProps & {
  readonly workspaceId: string;
  readonly folderId: string | null;
}) {
  const { organizationId, orgSegment, status } = useIManageArea();
  const navigate = useNavigate();
  const { library: resolved, libraries } = useIManageLibrary(organizationId, true, libraryOfId(workspaceId));
  const library = resolved ?? "";
  const libraryName = libraries.data?.entries.find((l) => l.id === library)?.name ?? library;
  const workspace = useContainer(organizationId, library, "matter", workspaceId);
  const folder = useContainer(organizationId, library, "folder", folderId);
  const children = useChildren(
    organizationId,
    library,
    folderId ? { id: folderId, kind: "folder" } : { id: workspaceId, kind: "matter" },
  );
  const query = search.q ?? "";
  const tab: IManageTab = search.tab ?? "documents";
  // The whole workspace's documents (top level), or a search inside here.
  const listing = useSearch(
    organizationId,
    library,
    query,
    query || !folderId ? (folderId ?? workspaceId) : null,
  );
  const [review, setReview] = useState<IManageDocument | null>(null);
  const favorites = useFavoriteRows(organizationId, library, { workspaceId, folderId });
  const [, setFavoriteList] = useIManageList(organizationId, "favorites");
  const workspaceStarred = favorites.isFavorite(
    workspace.data ?? { id: workspaceId, name: "", kind: "matter", version: null },
  );
  const startChat = useStartChatWithText(organizationId);

  const sort = sortFromSearch(search);
  const childParts = partitionEntries(children.data?.entries ?? []);
  const listParts = partitionEntries(listing.data?.entries ?? []);
  const filterFolders = (folders: IManageEntry[]) =>
    query ? folders.filter((f) => f.name.toLowerCase().includes(query.toLowerCase())) : folders;
  const useListing = query.length > 0 || !folderId;
  const documents = useListing ? listParts.documents : childParts.documents;
  const emails = useListing ? listParts.emails : childParts.emails;
  const folders = filterFolders(childParts.folders);

  const open = (entry: IManageEntry) => {
    if (entry.kind === "folder")
      void navigate({
        to: "/$org/apps/imanage/w/$workspaceId/$folderId",
        params: { org: orgSegment, workspaceId: entry.workspaceId ?? workspaceId, folderId: entry.id },
      });
    else if (entry.kind === "document") onSearchChange(patchSearch(search, { doc: entry.id, v: null }));
  };
  const setSort = (next: IManageSort) =>
    onSearchChange(patchSearch(search, { sort: next.key, dir: next.dir }), { replace: true });

  const loadingList = useListing ? listing.loading : children.loading;
  const listed = (useListing ? listing.data : children.data) !== null;
  const listError = useListing ? listing.error : children.error;
  const retry = useListing ? listing.refresh : children.refresh;
  const truncated =
    (useListing ? listing.data?.nextOffset : children.data?.nextOffset) !== null &&
    (useListing ? listing.data : children.data) !== null;
  const footer = truncated ? "Showing the first 50. Search here to find the rest." : undefined;

  const groups: IManageTableGroup[] =
    tab === "folders"
      ? [{ key: "folders", rows: sortEntries(folders, sort.key, sort.dir) }]
      : tab === "emails"
        ? [{ key: "emails", rows: sortEntries(emails, sort.key, sort.dir), ...(footer ? { footer } : {}) }]
        : [
            ...(folders.length && !query
              ? [{ key: "folders", label: "Folders", rows: sortEntries(folders, sort.key, sort.dir) }]
              : []),
            {
              key: "documents",
              ...(folders.length && !query
                ? { label: folderId ? "Documents" : "All documents in this workspace" }
                : {}),
              rows: sortEntries(documents, sort.key, sort.dir),
              ...(footer ? { footer } : {}),
            },
          ];

  const crumbs = (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-[13px]">
      <Link
        to="/$org/apps/imanage"
        params={{ org: orgSegment }}
        search={{ lib: library }}
        className="shrink-0 rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        {libraryName || "Library"}
      </Link>
      <Separator />
      {workspace.data ? (
        <Link
          to="/$org/apps/imanage/w/$workspaceId"
          params={{ org: orgSegment, workspaceId }}
          className={cn(
            "flex min-w-0 items-center gap-1.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
            folderId ? "text-muted-foreground hover:text-foreground" : "font-semibold text-foreground",
          )}
          aria-current={folderId ? undefined : "page"}
        >
          <MatterNumber entry={workspace.data} />
          <span className="truncate">{workspace.data.name}</span>
        </Link>
      ) : workspace.error ? (
        <span className="text-muted-foreground">Workspace unavailable</span>
      ) : (
        <Skeleton className="h-4 w-40" />
      )}
      {folderId ? (
        <FolderTrail
          organizationId={organizationId}
          orgSegment={orgSegment}
          library={library}
          workspaceId={workspaceId}
          folderId={folderId}
          current
        />
      ) : null}
    </nav>
  );

  const askMatter = () => {
    if (!startChat || !workspace.data) return;
    const w = workspace.data;
    void startChat(
      `About the iManage workspace ${[clientMatter(w), w.name].filter(Boolean).join(" ")} (workspace ID ${w.id}, library ${library}). You can browse it with imanage_list (libraryId ${library}, parentId ${w.id}, parentKind matter) and search it with imanage_search.\n\n`,
    );
  };

  const header = (
    <IManagePageHeader
      title={crumbs}
      actions={
        <>
          <IManageSearchField
            className="w-44 sm:w-64"
            value={query}
            placeholder={folder.data ? `Search ${folder.data.name}` : "Search this workspace"}
            onCommit={(q) => onSearchChange(patchSearch(search, { q }))}
          />
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={workspaceStarred ? "Remove workspace from favorites" : "Add workspace to favorites"}
                  aria-pressed={workspaceStarred}
                  disabled={!workspace.data}
                  className={cn(workspaceStarred && "text-warning")}
                  onClick={() =>
                    workspace.data &&
                    setFavoriteList((list) =>
                      toggledFavorite(list, refFromEntry(workspace.data!, { library }, Date.now())),
                    )
                  }
                />
              }
            >
              <StarIcon aria-hidden className={cn(workspaceStarred && "fill-current")} />
            </TooltipTrigger>
            <TooltipPopup>{workspaceStarred ? "Unstar workspace" : "Star workspace"}</TooltipPopup>
          </Tooltip>
        </>
      }
    >
      <IManageViewTabs<IManageTab>
        label="Workspace views"
        value={tab}
        onValueChange={(next) => onSearchChange(patchSearch(search, { tab: next === "documents" ? null : next }), { replace: true })}
        tabs={[
          { value: "documents", label: "Documents", ...(listed ? { count: documents.length } : {}) },
          { value: "emails", label: "Emails", ...(listed ? { count: emails.length } : {}) },
          { value: "folders", label: "Folders", ...(children.data ? { count: folders.length } : {}) },
          { value: "info", label: folderId ? "Folder info" : "Matter info" },
        ]}
      />
      {query ? (
        <span className="ms-auto pb-2.5 text-xs whitespace-nowrap text-muted-foreground">
          Results for “{query}” in {folder.data?.name ?? workspace.data?.name ?? "this workspace"}
        </span>
      ) : null}
    </IManagePageHeader>
  );

  if (review)
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {header}
        <IManageReviewPanel
          organizationId={organizationId}
          library={library}
          workspaceId={workspaceId}
          document={review}
          folder={folder.data ?? null}
          canUpload={status.canUpload}
          canSaveVersion={status.canSaveVersion}
          onClose={() => setReview(null)}
          onSaved={() => invalidateIManage(organizationId, ["browse", "search", "versions", "status"])}
        />
      </div>
    );

  const preview = search.doc ? (
    <IManagePreviewPane
      key={documentKey(search.doc)}
      organizationId={organizationId}
      orgSegment={orgSegment}
      library={library}
      documentId={search.doc}
      versionId={search.v ?? null}
      place={{ workspace: workspace.data, folder: folder.data }}
      canUpload={status.canUpload}
      canSaveVersion={status.canSaveVersion}
      canDownload={status.operations.includes("download")}
      onClose={() => onSearchChange(patchSearch(search, { doc: null, v: null }))}
      onSelectVersion={(v) => onSearchChange(patchSearch(search, { v }), { replace: true })}
      onReview={setReview}
      onSaved={() => invalidateIManage(organizationId, ["browse", "search", "versions", "document", "status"])}
    />
  ) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-imanage-workspace={workspaceId}>
      {header}
      <IManageSplit
        preview={preview}
        onClosePreview={() => onSearchChange(patchSearch(search, { doc: null, v: null }))}
      >
        {tab === "info" ? (
          <MatterInfo
            workspace={workspace.data}
            folder={folder.data}
            folderCount={childParts.folders.length}
            loading={workspace.loading}
            error={workspace.error}
            onRetry={workspace.refresh}
            library={libraryName}
            onAsk={startChat ? askMatter : null}
          />
        ) : listError && !(useListing ? listing.data : children.data) ? (
          <InlineError className="m-5" message={listError} onRetry={retry} />
        ) : (
          <IManageDocumentTable
            label={tab === "emails" ? "Emails" : tab === "folders" ? "Folders" : "Documents"}
            groups={groups}
            sort={sort}
            onSortChange={setSort}
            selectedId={search.doc ?? null}
            onOpen={open}
            loading={tab === "folders" ? children.loading : loadingList || (!useListing && children.loading)}
            isFavorite={favorites.isFavorite}
            onToggleFavorite={favorites.toggle}
            empty={
              query ? (
                <EmptyList
                  icon={<SearchXIcon />}
                  title={`Nothing matches “${query}”`}
                  body="Search looks at names and full text of documents and emails here, using your iManage permissions."
                />
              ) : tab === "emails" ? (
                <EmptyList icon={<MailIcon />} title="No emails filed here" />
              ) : tab === "folders" ? (
                <EmptyList icon={<FolderIcon />} title="No folders here" />
              ) : (
                <EmptyList art={Drawer} title="This folder is empty" />
              )
            }
          />
        )}
      </IManageSplit>
    </div>
  );
}

function Separator() {
  return <ChevronRightIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground/50" />;
}

/** The folders above `folderId`, read one parent at a time (bounded), then the folder itself. */
function FolderTrail({
  organizationId,
  orgSegment,
  library,
  workspaceId,
  folderId,
  current = false,
  depth = 0,
}: {
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly library: string;
  readonly workspaceId: string;
  readonly folderId: string;
  readonly current?: boolean;
  readonly depth?: number;
}) {
  const folder = useContainer(organizationId, library, "folder", folderId);
  const parent = folder.data?.parentId;
  return (
    <>
      {parent && parent !== workspaceId && depth < 6 ? (
        <FolderTrail
          organizationId={organizationId}
          orgSegment={orgSegment}
          library={library}
          workspaceId={workspaceId}
          folderId={parent}
          depth={depth + 1}
        />
      ) : null}
      <Separator />
      {folder.error && !folder.data ? (
        <span className="text-muted-foreground">Folder unavailable</span>
      ) : folder.data ? (
        <Link
          to="/$org/apps/imanage/w/$workspaceId/$folderId"
          params={{ org: orgSegment, workspaceId, folderId }}
          aria-current={current ? "page" : undefined}
          className={cn(
            "min-w-0 truncate rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
            current ? "font-semibold text-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {folder.data.name}
        </Link>
      ) : (
        <Skeleton className="h-4 w-20" />
      )}
    </>
  );
}

function MatterInfo({
  workspace,
  folder,
  folderCount,
  loading,
  error,
  onRetry,
  library,
  onAsk,
}: {
  readonly workspace: IManageEntry | null;
  readonly folder: IManageEntry | null;
  readonly folderCount: number;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly library: string;
  readonly onAsk: (() => void) | null;
}) {
  if (error && !workspace) return <InlineError className="m-5" message={error} onRetry={onRetry} />;
  if (loading || !workspace)
    return (
      <div className="flex flex-col gap-3 p-6">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-4 w-80" />
      </div>
    );
  const rows: Array<[string, ReactNode]> = [];
  const add = (label: string, value: ReactNode | undefined) => {
    if (value) rows.push([label, value]);
  };
  add(
    "Client",
    workspace.client ? (
      <>
        <span className="font-mono text-xs tabular-nums">{workspace.client}</span>
        {workspace.clientName ? ` · ${workspace.clientName}` : ""}
      </>
    ) : undefined,
  );
  add(
    "Matter",
    workspace.matter ? (
      <>
        <span className="font-mono text-xs tabular-nums">{workspace.matter}</span>
        {workspace.matterName ? ` · ${workspace.matterName}` : ""}
      </>
    ) : undefined,
  );
  add("Responsible", workspace.owner);
  add("Opened", workspace.created ? formatDateTime(workspace.created) : undefined);
  add("Last activity", workspace.modified ? formatDateTime(workspace.modified) : undefined);
  add("Folders", folderCount ? String(folderCount) : undefined);
  add("Library", library);
  add("Workspace ID", <span className="font-mono text-xs">{workspace.id}</span>);
  if (folder) {
    add("Folder", folder.name);
    add("Folder ID", <span className="font-mono text-xs">{folder.id}</span>);
  }
  return (
    <section className="mx-auto w-full max-w-3xl px-6 py-8" aria-label="Matter info" data-imanage-matter-info="">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs tabular-nums text-muted-foreground">{clientMatter(workspace)}</p>
          <h2 className="mt-1 text-xl font-semibold tracking-tight">{workspace.name}</h2>
          {workspace.description ? (
            <p className="mt-2 text-sm text-pretty text-muted-foreground">{workspace.description}</p>
          ) : null}
        </div>
        {onAsk ? (
          <Button size="sm" variant="outline" onClick={onAsk}>
            <SparklesIcon aria-hidden />
            Ask Amoreg about this matter
          </Button>
        ) : null}
      </div>
      <dl className="mt-6 grid grid-cols-[9rem_1fr] gap-x-4 gap-y-3 border-t border-border/60 pt-5 text-[13px]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="min-w-0 break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
